import queryString from "query-string";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { AUTH_PAGES_ROUTES } from "constants/routes";

import { AccessPageLayout } from "layouts/AccessPageLayout";

import { redirectToIdpLogin } from "_helpers/utils/idp";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useLanguage } from "hooks/useSelectedLanguage";

import SurfConextLogo from "assets/logo/logo-surf.png";

import { buildURLwithQueryParams } from "_helpers/utils/urlBuilder";
import { ToastMethods } from "components/ToastNotification";
import type { Lang } from "models/language/Language";
import {
  StyledButton,
  StyledLanguageSelector,
  StyledSURFconextButton,
  StyledSection,
  StyledSeparator,
} from "./SignInPage.styled";

export const SignInPage: React.FC = () => {
  const { t } = useTranslation("auth-pages");
  const navigate = useNavigate();
  const location = useLocation();
  const { enableSurfConext } = useFeatureFlags();
  const { currentLanguage, changeLanguage } = useLanguage();
  const [language, setLanguage] = useState(currentLanguage);

  const queryParamsPath = queryString.parse(location.search);

  useEffect(() => {
    if (queryParamsPath.error === "true") {
      ToastMethods.showToast(t("loginPage.toast.idpLoginError"), "error");
    }

    // If we got unverified teacher error, redirect to access denied page
    if (queryParamsPath.state === AUTH_PAGES_ROUTES.ACCESS_DENIED) {
      navigate(AUTH_PAGES_ROUTES.ACCESS_DENIED, { replace: true });
    }
  }, [queryParamsPath]);

  const onLanguageSelect = (selectedLanguage: keyof Lang) => {
    setLanguage(selectedLanguage);
    // update immediately after selecting
    changeLanguage(selectedLanguage);
  };

  const onSurfLoginButtonClick = () => {
    const surfConextLoginUrl = buildURLwithQueryParams("/api/generate-surf-oauth", [
      { key: "locationPath", value: location.pathname },
      { key: "queryParams", value: location.search },
    ]);

    // Redirect user to surfconext login page
    window.location.href = surfConextLoginUrl;
  };

  return (
    <AccessPageLayout>
      <StyledLanguageSelector
        selectedLanguage={language}
        selectLanguageLabel={t("loginPage.languageSelector.label", "Site language:")}
        onChangeLanguage={onLanguageSelect}
      />

      <StyledSection>
        {enableSurfConext && (
          <StyledSURFconextButton
            isBlock
            data-cy="surf-login-button"
            onClick={onSurfLoginButtonClick}
            variant="border"
          >
            <img alt={t("loginPage.surfConext.logoAlt")} src={SurfConextLogo} />
            {t("loginPage.surfConext.button", "Inloggen met SURFconext")}
          </StyledSURFconextButton>
        )}
        <StyledButton
          isBlock
          dataCy="idp-login-button"
          variant="primary"
          onClick={() => redirectToIdpLogin(queryParamsPath)}
        >
          {t("loginPage.existingAccount.button", "Login")}
        </StyledButton>
      </StyledSection>

      <StyledSeparator>{t("loginPage.separator.text", "or")}</StyledSeparator>

      <StyledSection>
        <StyledButton
          isBlock
          dataCy="register-button"
          variant="primary"
          onClick={() => navigate(AUTH_PAGES_ROUTES.REGISTER_ACCOUNT)}
        >
          {t("loginPage.noAccount.button", "Create account")}
        </StyledButton>
      </StyledSection>
    </AccessPageLayout>
  );
};
